import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Building2,
  FileText,
  MoreHorizontal,
  Pause,
  Play,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { api, ErroApi } from '../../../api/cliente';
import type {
  CertificadoA1,
  ConfigCaptura,
  EmpresaCaptura,
  PlanoCaptura,
} from '../../../api/tipos';
import {
  Aviso,
  Botao,
  Cartao,
  Celula,
  DivisorMenu,
  Esqueleto,
  Etiqueta,
  ItemMenu,
  Menu,
  Progresso,
  Tabela,
  Td,
  Th,
  cx,
  useConfirmar,
  useNotificar,
  Vazio,
  type TomEtiqueta,
} from '../../../componentes/ui';
import { Pagina, TituloPagina } from '../../../componentes/Pagina';
import {
  dataHora,
  numero,
  plural,
  reais,
  tempoRelativo,
} from '../../../util/formato';
import { AdicionarEmpresa } from './AdicionarEmpresa';
import {
  cnpjLegivel,
  dataCurta,
  diasParaVencer,
  DIAS_AVISO_CERTIFICADO,
  etiquetaCertificado,
  etiquetaEmpresa,
  faixaDoPlano,
  nomeDaFaixa,
} from './status';
import css from './Empresas.module.css';

const ATUALIZAR_MS = 30_000;

interface Pendencia {
  chave: string;
  tom: TomEtiqueta;
  texto: ReactNode;
  /** Botão da faixa. `Botao` + navegação, e não `BotaoLink`: o estilo de
   *  link do `Aviso` sublinharia o botão. */
  acao?: { rotulo: string; para: string };
}

/**
 * O que precisa de ação do cliente numa empresa. Fica em faixas acima da
 * tabela, e não dentro dela: numa linha de tabela o texto não quebra direito
 * e, no celular, ficaria escondido na rolagem horizontal.
 */
function pendencias(e: EmpresaCaptura): Pendencia[] {
  const lista: Pendencia[] = [];
  if (e.ultimoErro) {
    lista.push({
      chave: 'erro',
      tom: e.ativa ? 'alerta' : 'erro',
      texto: e.ultimoErro,
    });
  }
  const paraManifestar = e.notas.foraDoPrazo + e.notas.manifestacaoComErro;
  if (paraManifestar > 0) {
    lista.push({
      chave: 'manifestacao',
      tom: 'info',
      texto: `${plural(paraManifestar, 'nota precisa', 'notas precisam')} de manifestação pelo seu sistema emissor para liberar o XML.`,
      acao: {
        rotulo: 'Ver quais',
        para: `/painel/notas-recebidas?empresaId=${e.id}`,
      },
    });
  }
  if (e.certificado) {
    const dias = diasParaVencer(e.certificado.validoAte);
    if (dias > 0 && dias <= DIAS_AVISO_CERTIFICADO) {
      lista.push({
        chave: 'certificado',
        tom: 'alerta',
        texto: `O certificado vence em ${plural(dias, 'dia')} (${dataCurta(e.certificado.validoAte)}). Renove e envie o novo para a captura não parar.`,
      });
    }
  }
  return lista;
}

/**
 * Carteira de CNPJs na captura automática: o plano da conta, o status de cada
 * CNPJ, os certificados e o que está pendente de ação do cliente.
 */
export function Empresas() {
  const navegar = useNavigate();
  const confirmar = useConfirmar();
  const notificar = useNotificar();
  const [config, setConfig] = useState<ConfigCaptura | null>(null);
  const [plano, setPlano] = useState<PlanoCaptura | null>(null);
  const [empresas, setEmpresas] = useState<EmpresaCaptura[] | null>(null);
  const [certificados, setCertificados] = useState<CertificadoA1[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [adicionando, setAdicionando] = useState(false);
  const [ocupado, setOcupado] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    const [c, p, e, certs] = await Promise.all([
      api<ConfigCaptura>('/dfe/config'),
      api<PlanoCaptura>('/dfe/plano'),
      api<EmpresaCaptura[]>('/dfe/empresas'),
      api<CertificadoA1[]>('/dfe/certificados'),
    ]);
    setConfig(c);
    setPlano(p);
    setEmpresas(e);
    setCertificados(certs);
  }, []);

  useEffect(() => {
    let vivo = true;
    const ciclo = () =>
      carregar().catch((err) => {
        if (vivo)
          setErro(err instanceof ErroApi ? err.message : 'Falha ao carregar.');
      });
    void ciclo();
    const t = setInterval(() => void ciclo(), ATUALIZAR_MS);
    return () => {
      vivo = false;
      clearInterval(t);
    };
  }, [carregar]);

  async function acao(
    id: string,
    fn: () => Promise<unknown>,
    sucesso?: string,
  ) {
    setErro(null);
    setOcupado(id);
    try {
      await fn();
      if (sucesso) notificar(sucesso);
      await carregar();
    } catch (err) {
      notificar(
        err instanceof ErroApi
          ? err.message
          : 'Não foi possível concluir agora.',
        'erro',
      );
    } finally {
      setOcupado(null);
    }
  }

  const sincronizar = (e: EmpresaCaptura) => {
    // A SEFAZ só aceita uma nova consulta depois do intervalo; consultar antes
    // dá bloqueio de 1 hora. Por isso nem chamamos a API: só explicamos.
    if (!podeSincronizar(e)) {
      notificar(
        e.ativa
          ? `A SEFAZ só permite uma nova consulta depois do intervalo de 1 hora. Próxima a partir de ${dataHora(e.proximaConsultaEm)}.`
          : 'Reative a captura para consultar a SEFAZ.',
        'info',
      );
      return;
    }
    void acao(
      e.id,
      () => api(`/dfe/empresas/${e.id}/sincronizar`, { metodo: 'POST' }),
      'Consulta iniciada. As notas novas aparecem em instantes.',
    );
  };

  const alternar = async (e: EmpresaCaptura) => {
    if (e.ativa) {
      const ok = await confirmar({
        titulo: 'Pausar a captura?',
        mensagem:
          'A SEFAZ guarda as notas só por 90 dias e deixa de gerar novas para quem fica 60 dias sem consultar — uma pausa longa faz perder documentos.',
        confirmar: 'Pausar captura',
        cancelar: 'Voltar',
      });
      if (!ok) return;
    } else if (plano && plano.custoProximoCnpjCentavos > 0) {
      const ok = await confirmar({
        titulo: 'Reativar a captura?',
        mensagem: plano.ciclo
          ? `Com este CNPJ a conta passa da faixa paga. Serão debitados ${reais(plano.custoProximoCnpjCentavos)}, proporcionais aos dias até ${dataCurta(plano.ciclo.fim)}.`
          : `Serão debitados ${reais(plano.custoProximoCnpjCentavos)} dos seus créditos pelo plano do mês.`,
        confirmar: 'Reativar',
        cancelar: 'Voltar',
      });
      if (!ok) return;
    }
    void acao(
      e.id,
      () =>
        api(`/dfe/empresas/${e.id}`, {
          metodo: 'PATCH',
          corpo: { ativa: !e.ativa },
        }),
      e.ativa ? 'Captura pausada.' : 'Captura reativada.',
    );
  };

  const remover = async (e: EmpresaCaptura) => {
    const ok = await confirmar({
      titulo: `Remover ${e.nome}?`,
      mensagem: `Isso apaga ${e.notas.total === 1 ? 'a nota' : `as ${plural(e.notas.total, 'nota')}`} e todos os XMLs desta empresa no BAIXA XML. Baixe o ZIP antes se precisar deles.`,
      confirmar: 'Remover empresa',
      cancelar: 'Voltar',
      perigo: true,
    });
    if (!ok) return;
    void acao(
      e.id,
      () => api(`/dfe/empresas/${e.id}`, { metodo: 'DELETE' }),
      'Empresa removida.',
    );
  };

  const removerCertificado = async (c: CertificadoA1) => {
    const n = c._count?.empresas ?? 0;
    const ok = await confirmar({
      titulo: `Apagar o certificado de ${c.titular}?`,
      mensagem:
        n > 0
          ? `A captura ${n === 1 ? 'da empresa ligada' : `das ${plural(n, 'empresa')} ligadas`} a ele será pausada até você enviar outro.`
          : 'Ele é apagado de forma definitiva.',
      confirmar: 'Apagar certificado',
      cancelar: 'Voltar',
      perigo: true,
    });
    if (!ok) return;
    void acao(
      c.id,
      () => api(`/dfe/certificados/${c.id}`, { metodo: 'DELETE' }),
      'Certificado apagado.',
    );
  };

  const carregando = empresas === null || config === null;
  const comPendencia = empresas
    ? empresas
        .map((e) => ({ empresa: e, itens: pendencias(e) }))
        .filter((p) => p.itens.length > 0)
    : [];

  return (
    <Pagina largura="larga">
      <TituloPagina
        titulo="Captura automática"
        descricao="Todas as NF-e emitidas contra os CNPJs da sua carteira, direto da SEFAZ, sem precisar das chaves."
        acoes={
          config?.habilitado && (
            <Botao icone={<Plus />} onClick={() => setAdicionando(true)}>
              Adicionar empresa
            </Botao>
          )
        }
      />

      {erro && <Aviso>{erro}</Aviso>}
      {config && !config.habilitado && (
        <Aviso tom="info">
          A captura automática ainda está sendo liberada. Quer usar agora? Fale
          com a gente pelo <Link to="/painel/suporte">chat de suporte</Link>.
        </Aviso>
      )}

      {config?.habilitado && (
        <Cartao compacto>
          <div className={css.faixa}>
            <p className={css.comoFunciona}>
              <strong>Como funciona.</strong> Com o certificado A1 da empresa,
              consultamos a SEFAZ a cada hora (o intervalo que ela permite),
              registramos a Ciência da Operação e guardamos o XML original, com
              a assinatura do emissor. A SEFAZ disponibiliza as notas dos
              últimos 90 dias.
            </p>
            {plano && <ResumoPlano plano={plano} />}
          </div>
        </Cartao>
      )}

      {plano?.inadimplenteDesde && (
        <Aviso
          tom="alerta"
          titulo="Plano da captura em atraso"
          acao={
            <Botao
              aparencia="secundario"
              pequeno
              onClick={() => navegar('/painel/creditos')}
            >
              Recarregar
            </Botao>
          }
        >
          Não havia saldo para renovar o plano ({reais(plano.renovacaoCentavos)}
          ). A captura segue por {plural(plano.carenciaDias, 'dia')} desde{' '}
          {dataHora(plano.inadimplenteDesde)}; depois disso ela é pausada.
        </Aviso>
      )}

      {comPendencia.length > 0 && (
        <div className={css.pendencias}>
          {comPendencia.flatMap(({ empresa, itens }) =>
            itens.map((p) => (
              <Aviso
                key={`${empresa.id}-${p.chave}`}
                tom={p.tom}
                titulo={empresa.nome}
                acao={
                  p.acao && (
                    <Botao
                      aparencia="secundario"
                      pequeno
                      onClick={() => p.acao && navegar(p.acao.para)}
                    >
                      {p.acao.rotulo}
                    </Botao>
                  )
                }
              >
                {p.texto}
              </Aviso>
            )),
          )}
        </div>
      )}

      {carregando ? (
        <Cartao padding={0}>
          <div className={css.carregando}>
            <Esqueleto altura={20} />
            <Esqueleto altura={20} />
            <Esqueleto altura={20} />
          </div>
        </Cartao>
      ) : empresas.length === 0 ? (
        <Cartao padding={0}>
          <Vazio
            icone={<Building2 />}
            titulo="Nenhuma empresa na captura"
            descricao="Adicione o certificado A1 de um cliente e as notas de entrada dele começam a chegar sozinhas."
            acao={
              config.habilitado && (
                <Botao icone={<Plus />} onClick={() => setAdicionando(true)}>
                  Adicionar a primeira empresa
                </Botao>
              )
            }
          />
        </Cartao>
      ) : (
        <Cartao
          padding={0}
          titulo="Empresas"
          descricao={plural(
            empresas.length,
            'CNPJ monitorado',
            'CNPJs monitorados',
          )}
        >
          <Tabela rotulo="Empresas na captura">
            <thead>
              <tr>
                <Th>Empresa</Th>
                <Th>Situação</Th>
                <Th alinhar="direita">Notas</Th>
                <Th>Última consulta</Th>
                <Th>Certificado</Th>
                <Th encolher>
                  <span className="sr-only">Ações</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {empresas.map((e) => {
                const etiqueta = etiquetaEmpresa(e);
                const cert = e.certificado
                  ? etiquetaCertificado(e.certificado.validoAte)
                  : null;
                return (
                  <tr key={e.id}>
                    <Td className={css.semQuebra}>
                      <Celula
                        para={`/painel/notas-recebidas?empresaId=${e.id}`}
                        principal={e.nome}
                        secundaria={`CNPJ ${cnpjLegivel(e.cnpj)}`}
                      />
                    </Td>
                    <Td>
                      <Etiqueta
                        tom={etiqueta.tom}
                        title={e.ultimoErro ?? undefined}
                      >
                        {etiqueta.texto}
                      </Etiqueta>
                    </Td>
                    <Td alinhar="direita" className={css.notas}>
                      <span className={cx(css.notasTotal, 'num')}>
                        {numero(e.notas.total)}
                      </span>
                      <span className={css.notasDetalhe}>
                        <span className={css.completas}>
                          {numero(e.notas.completas)} com XML
                        </span>
                        {e.notas.aguardandoXml > 0 && (
                          <span>
                            {numero(e.notas.aguardandoXml)} aguardando
                          </span>
                        )}
                      </span>
                    </Td>
                    <Td className={css.semQuebra}>
                      <Celula
                        principal={
                          e.ultimaConsultaEm
                            ? tempoRelativo(e.ultimaConsultaEm)
                            : 'Ainda não houve'
                        }
                        secundaria={
                          e.ativa
                            ? `Próxima a partir de ${dataHora(e.proximaConsultaEm)}`
                            : 'Pausada'
                        }
                      />
                    </Td>
                    <Td className={css.semQuebra}>
                      {e.certificado && cert ? (
                        <Celula
                          principal={
                            <Etiqueta tom={cert.tom}>{cert.texto}</Etiqueta>
                          }
                          secundaria={`Até ${dataCurta(e.certificado.validoAte)}`}
                        />
                      ) : (
                        <Etiqueta tom="erro">Sem certificado</Etiqueta>
                      )}
                    </Td>
                    <Td encolher>
                      <Menu
                        gatilho={({ alternar: abrirMenu, ...aria }) => (
                          <Botao
                            aparencia="discreto"
                            pequeno
                            soIcone
                            icone={<MoreHorizontal />}
                            aria-label={`Ações de ${e.nome}`}
                            carregando={ocupado === e.id}
                            onClick={abrirMenu}
                            {...aria}
                          />
                        )}
                      >
                        <ItemMenu
                          icone={<FileText />}
                          onClick={() =>
                            navegar(`/painel/notas-recebidas?empresaId=${e.id}`)
                          }
                        >
                          Ver notas
                        </ItemMenu>
                        <ItemMenu
                          icone={<RefreshCw />}
                          onClick={() => sincronizar(e)}
                        >
                          {podeSincronizar(e)
                            ? 'Consultar agora'
                            : 'Consultar agora (aguarde o intervalo)'}
                        </ItemMenu>
                        <ItemMenu
                          icone={e.ativa ? <Pause /> : <Play />}
                          onClick={() => void alternar(e)}
                        >
                          {e.ativa ? 'Pausar captura' : 'Reativar captura'}
                        </ItemMenu>
                        <DivisorMenu />
                        <ItemMenu
                          icone={<Trash2 />}
                          perigo
                          onClick={() => void remover(e)}
                        >
                          Remover empresa
                        </ItemMenu>
                      </Menu>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Tabela>
        </Cartao>
      )}

      {certificados.length > 0 && (
        <Cartao
          padding={0}
          titulo="Certificados cadastrados"
          descricao="Os certificados ficam criptografados e nunca podem ser baixados de volta. Ao apagar, o arquivo é eliminado do nosso banco de dados."
        >
          <Tabela rotulo="Certificados A1">
            <thead>
              <tr>
                <Th>Titular</Th>
                <Th>Emissor</Th>
                <Th>Enviado em</Th>
                <Th alinhar="direita">Empresas</Th>
                <Th>Validade</Th>
                <Th encolher>
                  <span className="sr-only">Ações</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {certificados.map((c) => {
                const validade = etiquetaCertificado(c.validoAte);
                return (
                  <tr key={c.id}>
                    <Td className={css.semQuebra}>
                      <Celula
                        principal={c.titular}
                        secundaria={cnpjLegivel(c.cnpj)}
                      />
                    </Td>
                    <Td className={css.semQuebra}>
                      <span className={css.apagado}>{c.emissor}</span>
                    </Td>
                    <Td className={css.semQuebra}>
                      <span className={css.apagado}>
                        {dataHora(c.createdAt)}
                      </span>
                    </Td>
                    <Td alinhar="direita" className="num">
                      {c._count ? numero(c._count.empresas) : '—'}
                    </Td>
                    <Td className={css.semQuebra}>
                      <Celula
                        principal={
                          <Etiqueta tom={validade.tom}>
                            {validade.texto}
                          </Etiqueta>
                        }
                        secundaria={`Até ${dataCurta(c.validoAte)}`}
                      />
                    </Td>
                    <Td encolher>
                      <Botao
                        pequeno
                        aparencia="perigo"
                        icone={<Trash2 />}
                        carregando={ocupado === c.id}
                        onClick={() => void removerCertificado(c)}
                      >
                        Apagar
                      </Botao>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Tabela>
        </Cartao>
      )}

      {config?.habilitado && plano && adicionando && (
        <AdicionarEmpresa
          aberto={adicionando}
          aoFechar={() => setAdicionando(false)}
          aoCertificadoNovo={() => void carregar()}
          aoConcluir={(e) => {
            setAdicionando(false);
            notificar(
              `${e.nome} adicionada. A primeira consulta à SEFAZ já começou.`,
            );
            void carregar();
          }}
          certificados={certificados}
          custoCentavos={plano?.custoProximoCnpjCentavos ?? 0}
          plano={plano}
        />
      )}
    </Pagina>
  );
}

/** A SEFAZ só aceita nova consulta depois do intervalo de 1 hora. */
function podeSincronizar(e: EmpresaCaptura): boolean {
  return e.ativa && new Date(e.proximaConsultaEm) <= new Date();
}

/**
 * O plano da conta ao lado do "como funciona": a faixa paga e quanto dela está
 * em uso, ou — sem ciclo — a tabela de faixas.
 */
function ResumoPlano({ plano }: { plano: PlanoCaptura }) {
  if (!plano.ciclo) {
    return (
      <dl className={css.precos}>
        {plano.faixas.map((f) => (
          <div key={f.ate}>
            <dt>{nomeDaFaixa(f.ate, f.ate)}</dt>
            <dd className="num">
              {reais(f.centavos)}
              <span> /mês</span>
            </dd>
          </div>
        ))}
        <div>
          <dt>XMLs</dt>
          <dd>Ilimitados</dd>
        </div>
      </dl>
    );
  }

  const faixa = faixaDoPlano(
    plano.ciclo.cnpjsCobertos,
    plano.faixas,
    plano.adicionalCentavos,
  );
  return (
    <dl className={css.precos}>
      <div>
        <dt>Seu plano</dt>
        <dd className="num">
          {reais(plano.ciclo.valorCentavos)}
          <span>
            {' '}
            /mês · {nomeDaFaixa(faixa.ate, plano.ciclo.cnpjsCobertos)}
          </span>
        </dd>
      </div>
      <div className={css.uso}>
        <dt>CNPJs em uso</dt>
        <dd className="num">
          {numero(plano.cnpjsAtivos)} de {numero(plano.ciclo.cnpjsCobertos)}
        </dd>
        <Progresso
          valor={Math.min(
            1,
            plano.cnpjsAtivos / Math.max(1, plano.ciclo.cnpjsCobertos),
          )}
          rotulo="CNPJs em uso na faixa paga"
        />
      </div>
      <div>
        <dt>Renova em {dataCurta(plano.ciclo.fim)}</dt>
        <dd className="num">
          {reais(plano.renovacaoCentavos)}
          <span> · XMLs ilimitados</span>
        </dd>
      </div>
    </dl>
  );
}
